import {
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiFieldText,
  EuiForm,
  EuiFormRow,
  EuiText,
  EuiSpacer,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiPanel,
} from "@elastic/eui";
import { useEffect, useState } from "react";
import { AnketaResourcesEnum } from "../../../";
import { useApi, useAnketaI18n } from "../../../";
import { AnketaQuestionnaireBase } from "shared/index";
import { SelectQuestionnaireProps } from "../../";
import { useAnketaAuth } from "../../../auth/context";

export const SelectQuestionnaire = ({
  displayName,
  selectedQuestionnaires,
  setDisplayName,
  setSelectedQuestionnaires,
}: SelectQuestionnaireProps): JSX.Element => {
  const { translate } = useAnketaI18n();
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const [questionnaires, setQuestionnaires] = useState<
    Array<EuiComboBoxOptionOption<AnketaQuestionnaireBase>>
  >([]);
  const [isLoading, setIsLoading] = useState(true);

  const onChange = (
    selectedOptions: Array<EuiComboBoxOptionOption<AnketaQuestionnaireBase>>,
  ) => {
    setSelectedQuestionnaires(selectedOptions);
  };

  useEffect(() => {
    setIsLoading(true);
    Get<Array<AnketaQuestionnaireBase>>({
      path: `v2/${AnketaResourcesEnum.questionnaires}/catalogs`,
      responseType: "json",
      token: keycloak.token,
    })
      .then((response) => {
        const questionnairesComboBox: Array<
          EuiComboBoxOptionOption<AnketaQuestionnaireBase>
        > = response.data.map((res) => ({
          label: res.displayName,
          value: res,
        }));
        setQuestionnaires(questionnairesComboBox);
        if (questionnairesComboBox.length == 1) {
          onChange(questionnairesComboBox);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const hasSelection =
    selectedQuestionnaires && selectedQuestionnaires.length > 0;

  return (
    <EuiForm component="div">
      <EuiFlexGroup
        alignItems="center"
        gutterSize="s"
        style={{ marginBottom: 4 }}
      >
        <EuiFlexItem grow={false}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "linear-gradient(135deg, #0077CC 0%, #00BFB3 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <EuiIcon type="documents" color="white" size="s" />
          </div>
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiText size="s">
            <p style={{ margin: 0, color: "var(--aq-text-muted, #69707D)", lineHeight: 1.4 }}>
              {translate("anketa.questionnaire.select.helper") ||
                "Select a questionnaire template and provide a display name for this session."}
            </p>
          </EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size="l" />

      <EuiFormRow
        label={
          <span
            style={{ fontWeight: 600, fontSize: 13, letterSpacing: "0.02em" }}
          >
            {translate("anketa.questionnaire")}
          </span>
        }
        helpText={
          isLoading
            ? translate("anketa.loading") || "Loading available templates…"
            : !hasSelection
              ? translate("anketa.questionnaire.select.hint") ||
                "Choose one template to proceed"
              : undefined
        }
        fullWidth
      >
        <EuiComboBox
          singleSelection={{ asPlainText: true }}
          placeholder={translate("anketa.create.selection", {
            values: { type: translate("anketa.questionnaire") },
          })}
          options={questionnaires.length > 0 ? questionnaires : []}
          selectedOptions={hasSelection ? selectedQuestionnaires : []}
          onChange={onChange}
          noSuggestions={questionnaires.length === 0}
          isLoading={isLoading}
          fullWidth
          style={{ borderRadius: 6 }}
        />
      </EuiFormRow>

      <EuiSpacer size="m" />

      <EuiFormRow
        label={
          <span
            style={{ fontWeight: 600, fontSize: 13, letterSpacing: "0.02em" }}
          >
            {translate("anketa.displayName")}
          </span>
        }
        helpText={
          translate("anketa.displayName.helper") ||
          "This name will be shown to participants"
        }
        fullWidth
      >
        <EuiFieldText
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder={
            hasSelection
              ? selectedQuestionnaires[0].label
              : translate("anketa.displayName.placeholder") ||
                "Enter a display name…"
          }
          prepend={<EuiIcon type="tag" />}
          fullWidth
        />
      </EuiFormRow>

      {hasSelection && displayName.trim() !== "" && (
        <>
          <EuiSpacer size="l" />
          <EuiPanel
            color="subdued"
            borderRadius="m"
            paddingSize="m"
            style={{
              borderLeft: "3px solid #00BFB3",
              transition: "all 0.2s ease",
            }}
          >
            <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
              <EuiFlexItem grow={false}>
                <EuiIcon type="checkInCircleFilled" color="success" />
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiText size="s">
                  <strong>{displayName}</strong>
                  <span style={{ color: "var(--aq-text-muted, #69707D)", marginLeft: 6 }}>
                    · {selectedQuestionnaires[0].label}
                  </span>
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiPanel>
        </>
      )}
    </EuiForm>
  );
};
